import React, { useMemo } from 'react';
import {
  Grid,
  GridItem,
  Box,
  HStack,
  Icon,
  Badge,
  Text,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaTasks, FaProjectDiagram, FaCheckCircle } from 'react-icons/fa';
import { useTasks } from '../../hooks/useTasks';
import { useProjects } from '../../hooks/useProjects';

const DashboardStats: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');

  // Fetch data
  const { data: tasks, isLoading: tasksLoading, error: tasksError } = useTasks();
  const { data: projects, isLoading: projectsLoading, error: projectsError } = useProjects();

  // Calculate statistics from real data
  const stats = useMemo(() => {
    if (!tasks || !projects) {
      return [
        {
          icon: FaTasks,
          color: 'blue.500',
          value: '...',
          label: 'Active Tasks',
          badge: { text: 'Loading', colorScheme: 'gray' },
          trend: 'Loading data...',
        },
        {
          icon: FaProjectDiagram,
          color: 'green.500',
          value: '...',
          label: 'Active Projects',
          badge: { text: 'Loading', colorScheme: 'gray' },
          trend: 'Loading data...',
        },
        {
          icon: FaCheckCircle,
          color: 'purple.500',
          value: '...',
          label: 'Completed Tasks',
          badge: { text: 'Loading', colorScheme: 'gray' },
          trend: 'Loading data...',
        },
      ];
    }

    // Calculate active tasks (not completed)
    const activeTasks = tasks.filter(task => task.status !== 'Completed');

    // Calculate completed tasks
    const completedTasks = tasks.filter(task => task.status === 'Completed');

    // Calculate active projects (assuming a project is active if it has active tasks)
    const activeProjectIds = new Set(
      activeTasks
        .map(task => task.project)
        .filter(Boolean)
        .map(project => (typeof project === 'string' ? project : project!._id))
    );

    // Calculate trends (this is simplified - in a real app you'd compare with previous periods)
    const getTaskTrend = () => {
      const totalTasks = tasks.length;
      if (totalTasks === 0) return 'No tasks yet';

      const completionRate = Math.round((completedTasks.length / totalTasks) * 100);
      return `${completionRate}% completion rate`;
    };

    const getProjectTrend = () => {
      if (projects.length === 0) return 'No projects yet';
      if (activeProjectIds.size === projects.length) return 'All projects active';
      return `${activeProjectIds.size} of ${projects.length} active`;
    };

    const getCompletedTrend = () => {
      if (completedTasks.length === 0) return 'No completed tasks yet';
      const recentCompleted = completedTasks.filter(task => {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return new Date(task.updatedAt) > weekAgo;
      });
      return recentCompleted.length > 0
        ? `+${recentCompleted.length} completed this week`
        : 'No recent completions';
    };

    return [
      {
        icon: FaTasks,
        color: 'blue.500',
        value: activeTasks.length.toString(),
        label: 'Active Tasks',
        badge: {
          text: activeTasks.length > 0 ? 'Active' : 'None',
          colorScheme: activeTasks.length > 0 ? 'blue' : 'gray',
        },
        trend: getTaskTrend(),
      },
      {
        icon: FaProjectDiagram,
        color: 'green.500',
        value: activeProjectIds.size.toString(),
        label: 'Active Projects',
        badge: {
          text: activeProjectIds.size > 0 ? 'Running' : 'None',
          colorScheme: activeProjectIds.size > 0 ? 'green' : 'gray',
        },
        trend: getProjectTrend(),
      },
      {
        icon: FaCheckCircle,
        color: 'purple.500',
        value: completedTasks.length.toString(),
        label: 'Completed Tasks',
        badge: {
          text: completedTasks.length > 0 ? 'Done' : 'None',
          colorScheme: completedTasks.length > 0 ? 'purple' : 'gray',
        },
        trend: getCompletedTrend(),
      },
    ];
  }, [tasks, projects]);

  // Loading state
  if (tasksLoading || projectsLoading) {
    return (
      <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6} mb={8}>
        {[1, 2, 3].map(index => (
          <GridItem key={index}>
            <Box
              bg={cardBg}
              p={8}
              borderRadius="2xl"
              boxShadow="lg"
              border="1px"
              borderColor="gray.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              minH="200px"
            >
              <Spinner size="lg" color="blue.500" />
            </Box>
          </GridItem>
        ))}
      </Grid>
    );
  }

  // Error state
  if (tasksError || projectsError) {
    return (
      <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6} mb={8}>
        <GridItem colSpan={3}>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            Failed to load dashboard statistics. Please try again later.
          </Alert>
        </GridItem>
      </Grid>
    );
  }

  return (
    <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6} mb={8}>
      {stats.map((stat, index) => (
        <GridItem key={index}>
          <Box
            bg={cardBg}
            p={8}
            borderRadius="2xl"
            boxShadow="lg"
            border="1px"
            borderColor="gray.100"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}
          >
            <HStack justify="space-between" mb={4}>
              <Icon as={stat.icon} boxSize={12} color={stat.color} />
              <Badge
                colorScheme={stat.badge.colorScheme}
                variant="subtle"
                fontSize="sm"
                px={3}
                py={1}
              >
                {stat.badge.text}
              </Badge>
            </HStack>
            <Text fontSize="3xl" fontWeight="bold" color="gray.800" mb={1}>
              {stat.value}
            </Text>
            <Text color="gray.500" fontSize="lg">
              {stat.label}
            </Text>
            <Text color="green.500" fontSize="sm" fontWeight="medium" mt={2}>
              {stat.trend}
            </Text>
          </Box>
        </GridItem>
      ))}
    </Grid>
  );
};

export default DashboardStats;
